/**
 * Super Admin List Component
 * 
 * Displays a read-only list of users with super admin privileges.
 * No edit functionality - just informational.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchSuperAdmins } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Shield, User, Loader2 } from 'lucide-react';
import { useLanguage } from '@/core/LanguageContext';

export function SuperAdminList() {
  const { t } = useLanguage();

  const { data: superAdmins = [], isLoading, error } = useQuery({
    queryKey: ['superadmin', 'super-admins'],
    queryFn: fetchSuperAdmins,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">
            {t('superadmin.error.title', 'Error')}
          </CardTitle>
          <CardDescription>
            {t('superadmin.error.loadingAdmins', 'Failed to load super admins')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">
          {t('superadmin.admins.title', 'Super Administrators')}
        </h2>
        <Badge variant="secondary">{superAdmins.length}</Badge>
      </div>
      
      <p className="text-sm text-muted-foreground">
        {t('superadmin.admins.description', 'Users with full system access. Super admin status can only be modified directly in the database.')}
      </p>

      {superAdmins.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Shield className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {t('superadmin.admins.empty', 'No super admins found')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {superAdmins.map((admin) => (
            <Card key={admin.id} className="hover:bg-accent/50 transition-colors">
              <CardContent className="flex items-center gap-4 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {admin.full_name || admin.email}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {admin.email}
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0">
                  <Shield className="h-3 w-3 mr-1" />
                  {t('superadmin.admins.badge', 'Super Admin')}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
